import { lazy, Suspense } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router'

import { AuditPage } from '@/features/audit/AuditPage'
import { usePermissions } from '@/features/auth'
import {
  useHasPermissionFn,
  useIsCarrierAccount,
  useRole,
} from '@/features/auth/hooks'
import { BookingPage } from '@/features/book/BookingPage'
import { CustomerRoutedBookingPage } from '@/features/book/CustomerRoutedBookingPage'
import { TLBookingPage } from '@/features/booking/TLBookingPage'
import { TLCustomerRoutedBookingPage } from '@/features/booking/TLCustomerRoutedBookingPage'
import { NotificationsPage } from '@/features/notifications/NotificationsPage'
import { useOnboardingContext } from '@/features/onboarding/OnboardingProvider'
import { ProvisionerAccountPage } from '@/features/provisioner/ProvisionerAccountPage'
import { ProvisionerPage } from '@/features/provisioner/ProvisionerPage'
import { QuotePage } from '@/features/quote/QuotePage'
import { RatesPage } from '@/features/quote/RatesPage'
import { QuoteRequestForm } from '@/features/quote-request/QuoteRequestForm'
import { ShipmentBookPage } from '@/features/shipment-form/ShipmentBookPage'
import { ShipmentCreatePage } from '@/features/shipment-form/ShipmentCreatePage'
import { ShipmentEditPage } from '@/features/shipment-form/ShipmentEditPage'
import { ActiveShipmentsPage } from '@/features/shipment-list/ActiveShipmentsPage'
import { InProcessPage } from '@/features/shipment-list/InProcessPage'
import { useHomePage } from '@/route-hooks'

import InfoBox from './InfoBox'
import { Layout } from './layout/Layout'
import { QuoteRequestDetail } from './QuoteRequest/QuoteRequestDetail'
import SearchResults from './SearchResults'
import ShipmentDetailsPage from './ShipmentDetails'
import { SubAppSuspense } from './SubAppLoading'

const OnboardingApp = lazy(() => import('@/features/onboarding/OnboardingApp'))
const SettingsApp = lazy(() => import('@/features/settings/SettingsApp'))
const AnalyticsApp = lazy(() => import('@/features/analytics/AnalyticsApp'))
const ReportsApp = lazy(() => import('@/features/reports/ReportsApp'))
const DashboardApp = lazy(() => import('@/features/dashboard/DashboardApp'))

export const ShipperRoutes = () => {
  const hasPermission = useHasPermissionFn()
  const landingPage = useHomePage()
  const { canAudit, canBook, canQuote } = usePermissions()
  const role = useRole()
  const isCarrier = useIsCarrierAccount()
  const {
    onboarding: [isOnboarding],
  } = useOnboardingContext()

  if (isOnboarding) {
    return (
      <Routes>
        <Route
          path="welcome/*"
          element={
            <Suspense fallback={<InfoBox icon="sync">Loading...</InfoBox>}>
              <OnboardingApp />
            </Suspense>
          }
        />
        <Route path="*" element={<Navigate to="welcome" replace />} />
      </Routes>
    )
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route
          path="settings/*"
          element={
            <SubAppSuspense>
              <SettingsApp />
            </SubAppSuspense>
          }
        />
        <Route path="active" element={<ActiveShipmentsPage />} />

        {hasPermission('analytics-viewer') && (
          <Route
            path="analytics/*"
            element={
              <SubAppSuspense>
                <AnalyticsApp />
              </SubAppSuspense>
            }
          />
        )}

        <Route path="notifications" element={<NotificationsPage />} />

        <Route path="details/:loadId" element={<ShipmentDetailsPage />} />

        {canAudit && <Route path="audit" element={<AuditPage />} />}

        {(canQuote || canBook) && (
          <>
            <Route path="pending" element={<InProcessPage />}>
              <Route path="uploads">
                <Route index element={null} />
                <Route path="new" element={null} />
                <Route path=":uploadId" element={null} />
              </Route>
            </Route>
            <Route path="pending/customer-routed">
              <Route index element={<TLCustomerRoutedBookingPage />} />
              <Route path=":loadId" element={<TLCustomerRoutedBookingPage />} />
            </Route>
            <Route path="pending/:quoteRequestId" element={<TLBookingPage />} />
          </>
        )}

        <Route
          path="quote/:quoteRequestId"
          element={
            <div className="shipper-page-fill grid bg-[#f2f2f2] grid-rows-[minmax(0,1fr)] grid-pending-detail-tl grid-cols-[minmax(20rem,0.3fr)_1fr_minmax(16rem,0.4fr)]">
              <QuoteRequestDetail />
            </div>
          }
        />

        <Route
          path="quote-request"
          element={
            <div className="shipper-page-fill shipper-grid-rows-main-footer grid grid-cols-[15rem_22rem_1fr] bg-[#f2f2f2] b1500:grid-cols-[15rem_20rem_1fr] b1350:grid-rows-[auto_auto_minmax(0,1fr)_auto] b1350:grid-cols-[21rem_1fr]">
              <QuoteRequestForm />
            </div>
          }
        >
          <Route path=":quoteRequestId" element={null}>
            <Route path=":mode" element={null} />
          </Route>
        </Route>

        <Route path=":workflow/quote" element={<QuotePage />}>
          <Route path=":loadId" element={null}>
            <Route path=":mode" element={null} />
          </Route>
        </Route>

        <Route path="rates/:loadId" element={<RatesPage />}>
          <Route path=":mode" element={null} />
        </Route>
        <Route path="create" element={<ShipmentCreatePage />} />
        <Route
          path="shipments/:loadId/book/:quoteId"
          element={<ShipmentBookPage />}
        />
        <Route path="shipments/:loadId/edit" element={<ShipmentEditPage />} />
        <Route
          path="book"
          element={
            <div className="shipper-page-fill grid grid-cols-[20rem_1fr] grid-rows-[minmax(0,1fr)_auto] bg-fv-gray-100">
              <Outlet />
            </div>
          }
        >
          <Route
            path="customer-routed"
            element={
              canBook ? (
                <CustomerRoutedBookingPage />
              ) : (
                <Navigate to="ltl/quote" replace />
              )
            }
          />
          <Route path=":loadId/:quoteId" element={<BookingPage />} />
        </Route>

        <Route path="search" element={<SearchResults />} />

        {isCarrier && (
          <>
            <Route path="provisioning">
              <Route
                path=""
                element={<Navigate to="/provisioning/requested" replace />}
              />
              <Route path=":status" element={<ProvisionerPage />} />
              <Route
                path="account/:accountId"
                element={<ProvisionerAccountPage />}
              />
            </Route>
            <Route
              path="*"
              element={<Navigate to="/provisioning/requested" replace />}
            />
          </>
        )}
        <Route
          path="dashboard/*"
          element={
            <SubAppSuspense>
              <DashboardApp />
            </SubAppSuspense>
          }
        />
        <Route
          path="*"
          element={
            role === 'vendor' ? (
              <Navigate to="/create" replace />
            ) : (
              <Navigate to={landingPage} replace />
            )
          }
        />
      </Route>
      {hasPermission('analytics-viewer') && (
        <Route
          path="reports/*"
          element={
            <div className="h-dvh min-h-0 min-w-0">
              <SubAppSuspense>
                <ReportsApp />
              </SubAppSuspense>
            </div>
          }
        />
      )}
      <Route
        path="welcome/*"
        element={
          <Suspense fallback={<InfoBox icon="sync">Loading...</InfoBox>}>
            <OnboardingApp />
          </Suspense>
        }
      />
    </Routes>
  )
}
